import { useState, useRef, useEffect, useCallback } from "react";
import {
  Download, Share2, RotateCcw, Loader2, Check,
  Wand2, Eye, EyeOff, Pause, Play
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/hooks/use-toast";
import { useDownload } from "@/hooks/use-download";
import { useSubscription } from "@/hooks/use-subscription";
import { useSession } from "@/hooks/use-session";
import { capture } from "@/lib/analytics";
import { waitForJob } from "@/lib/jobs";
import { getSlug } from "@/lib/utils";
import type { GenerateResponse, HistoryEntry, Version } from "@/lib/types";
import { PricingModal } from "@/components/PricingModal";

interface SVGViewerProps {
  result: GenerateResponse;
  history: HistoryEntry[];
  versions: Version[];
  versionIndex: number;
  onRefineResult: (data: GenerateResponse, prompt: string) => void;
  onRevert: (index: number) => void;
  onReset: () => void;
}

export function SVGViewer({
  result, history, versions, versionIndex,
  onRefineResult, onRevert, onReset,
}: SVGViewerProps) {
  const { session } = useSession();
  const { downloading, handleDownload, downloadUrl, purchased } = useDownload(result.id);
  const { subscription } = useSubscription(session?.user?.id);
  const [showPricing, setShowPricing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [refinePrompt, setRefinePrompt] = useState("");
  const [refining, setRefining] = useState(false);
  const [isPublic, setIsPublic] = useState(result.is_public ?? true);
  const [togglingVisibility, setTogglingVisibility] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [selectedElementSvg, setSelectedElementSvg] = useState<string | null>(null);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Clear selection when result changes
  useEffect(() => {
    setSelectedElementSvg(null);
    setSelectedElementId(null);
  }, [result.id, result.versionId]);

  // Sync pause state with SVG
  useEffect(() => {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;

    if (isPaused) {
      svg.pauseAnimations();
      svg.classList.add("paused-svg");
    } else {
      svg.unpauseAnimations();
      svg.classList.remove("paused-svg");
    }
  }, [isPaused, result.svg]);

  // Apply highlight to selected element
  useEffect(() => {
    const svg = containerRef.current?.querySelector("svg");
    if (!svg) return;

    // Clear previous selection highlight
    svg.querySelectorAll("[data-selected]").forEach((el) => {
      el.removeAttribute("data-selected");
      (el as HTMLElement).style.outline = "";
    });

    if (selectedElementId) {
      const el = svg.querySelector(`[data-selection-id="${selectedElementId}"]`) as HTMLElement;
      if (el) {
        el.setAttribute("data-selected", "true");
        el.style.outline = "2px solid hsl(var(--primary))";
        el.style.outlineOffset = "2px";
      }
    }
  }, [selectedElementId, result.svg]);

  // Handle element selection
  const handleSvgClick = useCallback((e: React.MouseEvent) => {
    const target = e.target as SVGElement;
    if (target.tagName.toLowerCase() === "svg") {
      setSelectedElementId(null);
      setSelectedElementSvg(null);
      return;
    }

    // Assign a temporary selection ID if it doesn't have one
    let selId = target.getAttribute("data-selection-id");
    if (!selId) {
      selId = Math.random().toString(36).slice(2, 9);
      target.setAttribute("data-selection-id", selId);
    }
    
    // Get actual SVG part
    const svgPart = target.outerHTML;
    setSelectedElementSvg(svgPart);
    setSelectedElementId(selId);

    // Visual feedback
    const name = target.id || target.getAttribute("data-name") || target.tagName.toLowerCase();
    toast({
      title: "Element selected",
      description: `Targeting ${name} for next refinement.`,
    });

    // Pause animation when selecting
    setIsPaused(true);

    // Auto-focus textarea
    textareaRef.current?.focus();
  }, []);

  useEffect(() => {
    if (result.is_public !== undefined) {
      setIsPublic(result.is_public);
    }
  }, [result.is_public]);

  const isOwner = session?.user?.id && result.user_id === session.user.id;

  const handleToggleVisibility = async () => {
    if (!result.id || !isOwner) return;
    setTogglingVisibility(true);
    try {
      const nextPublic = !isPublic;
      const res = await fetch(`/api/visibility/${result.id}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isPublic: nextPublic }),
      });
      if (!res.ok) throw new Error("Failed to update visibility");
      setIsPublic(nextPublic);
      toast({ 
        title: nextPublic ? "Animation public" : "Animation private",
        description: nextPublic ? "It now appears in the community showcase." : "It is now hidden from the community."
      });
    } catch {
      toast({ variant: "destructive", title: "Error", description: "Could not update visibility." });
    } finally {
      setTogglingVisibility(false);
    }
  };

  useEffect(() => {
    if (downloadUrl) {
      // Automatic download
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `animation-${result.id.slice(0, 8)}.svg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }, [downloadUrl, result.id]);

  const handleRefine = async (e: React.FormEvent) => {
    e.preventDefault();
    let trimmed = refinePrompt.trim();
    if (!trimmed || refining) return;

    // Append selected element if exists (hidden from user)
    if (selectedElementSvg) {
      trimmed = `${trimmed}\n\nAdjust this element:\n\`\`\`xml\n${selectedElementSvg}\n\`\`\``;
    }

    const refinementNumber = versions.length; // 1-based after this completes
    capture("refine_started", {
      animation_id: result.id,
      refinement_number: refinementNumber,
      prompt_length: trimmed.length,
    });

    setRefining(true);
    const startedAt = Date.now();
    try {
      const res = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: trimmed,
          history,
          isRefinement: true,
          originalId: result.id,
        }),
      });
      
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Unknown error" }));
        const errorMessage = (err as { error?: string }).error ?? "Refinement failed";
        
        if (res.status === 403 && errorMessage.includes("limit")) {
          setShowPricing(true);
          return;
        }
        
        throw new Error(errorMessage);
      }

      const { job_id } = (await res.json()) as { job_id: string };

      const job = await waitForJob(job_id);
      if (job.status === "failed") {
        throw new Error(job.error ?? "Refinement failed");
      }
      if (!job.animation_id) {
        throw new Error("Refinement did not return an animation id");
      }

      const svgRes = await fetch(`/api/svg/${job.animation_id}`);
      if (!svgRes.ok) throw new Error("Failed to load refined SVG");
      const svg = await svgRes.text();

      const data: GenerateResponse = { 
        id: job.animation_id, 
        prompt: trimmed, 
        svg,
        user_id: result.user_id,
        is_public: isPublic,
      };
      capture("refine_completed", {
        animation_id: job.animation_id,
        original_id: result.id,
        refinement_number: refinementNumber,
        duration_ms: Date.now() - startedAt,
      });
      onRefineResult(data, trimmed);
      setRefinePrompt("");
    } catch (err) {
      capture("refine_failed", {
        animation_id: result.id,
        refinement_number: refinementNumber,
        error: err instanceof Error ? err.message : String(err),
      });
      toast({
        variant: "destructive",
        title: "Refinement failed",
        description: err instanceof Error ? err.message : "Please try again.",
      });
    } finally {
      setRefining(false);
    }
  };

  const handleShare = async () => {
    const slug = getSlug(versions[0]?.prompt ?? result.prompt, result.id);
    const url = `${window.location.origin}/v/${slug}`;
    capture("share_clicked", { animation_id: result.id, slug });
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: "Link copied!" });
    } catch {
      toast({ title: "Share link", description: url });
    }
  };

  const handleRestyle = async () => {
    if (subscription?.plan !== "enterprise") return;
    setRefining(true);
    try {
      const res = await fetch(`/api/user/restyle/${result.id}`, { method: "POST" });
      if (!res.ok) throw new Error("Restyle failed");
      const { restyle_prompt } = await res.json();
      
      const genRes = await fetch("/api/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: restyle_prompt,
          history,
          isRefinement: true,
          originalId: result.id,
        }),
      });

      if (!genRes.ok) throw new Error("Restyle failed to start");
      const { job_id } = await genRes.json();
      const updated = await waitForJob(job_id);
      
      const svgRes = await fetch(`/api/svg/${updated.version_id}`);
      const svg = await svgRes.text();

      onRefineResult({
        id: result.id,
        versionId: updated.version_id!,
        prompt: restyle_prompt,
        svg,
        user_id: result.user_id,
        is_public: isPublic,
      }, restyle_prompt);
      toast({ title: "Restyled!", description: "Applied your brand guidelines to this animation." });
    } catch (err) {
      console.error(err);
      toast({ variant: "destructive", title: "Error", description: "Could not restyle animation." });
    } finally {
      setRefining(false);
    }
  };

  return (
    <div className="w-full max-w-5xl mx-auto space-y-12 animate-fade-in pb-20">
      <style>{`
        .paused-svg *, .paused-svg {
          animation-play-state: paused !important;
        }
        .svg-container svg *:hover {
          outline: 1.5px solid hsl(var(--primary) / 0.5);
          outline-offset: 1px;
          cursor: crosshair;
        }
        .svg-container svg *[data-selected="true"] {
          outline: 2px solid hsl(var(--primary)) !important;
          outline-offset: 2px;
        }
      `}</style>
      
      <div className="flex flex-col lg:flex-row gap-12 items-start">
        {/* Left Side: Preview */}
        <div className="flex-1 w-full space-y-6">
          <div className="relative group svg-container aspect-square rounded-[2rem] bg-muted/20 border border-border/50 shadow-2xl overflow-hidden">
            <div
              ref={containerRef}
              onClick={handleSvgClick}
              className={`w-full h-full flex items-center justify-center p-12 transition-all duration-500 ${refining ? "opacity-30 scale-95 blur-sm" : "opacity-100"}`}
              dangerouslySetInnerHTML={{ __html: result.svg }}
            />
            
            {/* Play/Pause Button */}
            <div className={`absolute bottom-6 right-6 transition-all duration-300 ${isPaused ? "opacity-100 scale-110" : "opacity-0 group-hover:opacity-100 scale-100"}`}>
              <button
                type="button"
                className={`flex h-12 w-12 items-center justify-center rounded-full border border-white/10 transition-all ${!isPaused ? "bg-black/60 hover:bg-black/80 text-white" : "bg-foreground text-background shadow-xl"}`}
                onClick={(e) => { e.stopPropagation(); setIsPaused(!isPaused); }}
              >
                {isPaused ? <Play className="h-5 w-5 fill-current" /> : <Pause className="h-5 w-5 fill-current" />}
              </button>
            </div>

            {refining && (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-background/20 backdrop-blur-sm">
                <Loader2 className="h-10 w-10 animate-spin text-foreground" />
                <p className="text-sm font-bold uppercase tracking-widest text-foreground">Refining...</p>
              </div>
            )}

            <div className="absolute top-6 left-6 flex items-center gap-3">
              <div className="px-4 py-1.5 rounded-full bg-background/80 backdrop-blur-md border border-border/10 text-[10px] font-bold uppercase tracking-widest shadow-sm">
                Live Preview
              </div>
              {isOwner && (
                <button 
                  className={`px-4 py-1.5 rounded-full backdrop-blur-md border transition-all text-[10px] font-bold uppercase tracking-widest shadow-sm flex items-center gap-2 ${isPublic ? "bg-background/80 border-border/10 text-primary" : "bg-foreground text-background border-transparent"}`}
                  onClick={handleToggleVisibility}
                >
                  {togglingVisibility ? <Loader2 className="h-3 w-3 animate-spin" /> : (
                    isPublic ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />
                  )}
                  {isPublic ? "Public" : "Private"}
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between px-2">
            <div className="flex items-center gap-4">
              <button 
                onClick={handleShare}
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
              >
                {copied ? <Check className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
                {copied ? "Copied" : "Share"}
              </button>
              <div className="h-1 w-1 rounded-full bg-border" />
              <button 
                onClick={() => { capture("start_over", { animation_id: result.id }); onReset(); }}
                className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-muted-foreground hover:text-destructive transition-colors"
              >
                <RotateCcw className="h-3.5 w-3.5" />
                Discard
              </button>
            </div>
            
            {versions.length > 1 && (
               <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-muted/50 border border-border/10 text-[10px] font-bold text-muted-foreground">
                 v{versionIndex + 1} / {versions.length}
               </div>
            )}
          </div>
        </div>

        {/* Right Side: Controls */}
        <div className="w-full lg:w-[400px] space-y-8">
          <div className="space-y-4">
            <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Refine this animation</h3>
            <form 
              onSubmit={handleRefine} 
              className="relative rounded-[1.25rem] bg-card border border-border/40 p-5 shadow-xl space-y-4 focus-within:border-primary/30 transition-all duration-300"
            >
              <Textarea
                ref={textareaRef}
                placeholder='e.g. "Make the colors more vibrant" or "Add a pulsing glow effect"'
                value={refinePrompt}
                onChange={(e) => setRefinePrompt(e.target.value)}
                className="min-h-[120px] text-lg border-none bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0 resize-none placeholder:text-muted-foreground/30 p-0 shadow-none outline-none"
                disabled={refining}
              />
                
              <button
                type="submit"
                disabled={!refinePrompt.trim() || refining}
                className="w-full rounded-full h-12 bg-foreground text-background font-bold text-sm transition-all hover:opacity-90 active:scale-95 disabled:opacity-30 flex items-center justify-center gap-2"
              >
                {refining ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {refining ? "Refining..." : "Apply refinement"}
              </button>
            </form>
          </div>

          {subscription?.plan === "enterprise" && (
            <div className="space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Intelligence</h3>
              <button 
                onClick={handleRestyle}
                className="w-full flex h-14 items-center justify-center gap-3 rounded-[1.25rem] border border-border/10 bg-primary/5 text-primary text-sm font-bold transition-all hover:bg-primary/10 active:scale-98"
              >
                Apply Brand Style
              </button>
            </div>
          )}

          <div className="space-y-4">
             <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Download</h3>
             <div className="grid grid-cols-1 gap-3">
               {downloadUrl ? (
                  <a
                    href={downloadUrl}
                    className="flex h-14 items-center justify-center gap-3 rounded-[1.25rem] bg-muted/30 border border-border/10 text-sm font-bold transition-all hover:bg-muted/50 active:scale-98"
                  >
                    <Download className="h-4 w-4" />
                    Download SVG
                  </a>
                ) : (
                  <button 
                    onClick={handleDownload} 
                    disabled={downloading} 
                    className="flex h-14 items-center justify-center gap-3 rounded-[1.25rem] bg-muted/30 border border-border/10 text-sm font-bold transition-all hover:bg-muted/50 active:scale-98"
                  >
                    {downloading ? <Loader2 className="animate-spin" /> : <Download className="h-4 w-4" />}
                    {purchased ? "Download SVG" : "Download SVG — $5"}
                  </button>
                )}
             </div>
          </div>

          {versions.length > 1 && (
            <div className="space-y-4">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Version history</h3>
              <div className="rounded-[1.25rem] border border-border/10 bg-muted/10 divide-y divide-border/10 overflow-hidden">
                {versions.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => onRevert(i)}
                    className={`w-full flex items-center justify-between px-5 py-4 text-left transition-colors ${i === versionIndex ? "bg-muted/50 pointer-events-none" : "hover:bg-muted/30"}`}
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <span className="text-[10px] font-bold text-muted-foreground/50 w-4 shrink-0">0{i + 1}</span>
                      <p className="text-xs font-medium text-foreground/70 truncate">
                        {v.prompt}
                      </p>
                    </div>
                    {i === versionIndex && (
                      <div className="h-1.5 w-1.5 rounded-full bg-primary shadow-[0_0_8px_hsl(var(--primary))]" />
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <PricingModal 
        open={showPricing} 
        onClose={() => setShowPricing(false)} 
        currentPlan={subscription?.plan} 
      />
    </div>
  );
}
